/**
 * @fileoverview Reusable Integration Card Component
 * Displays integration status, connect/disconnect actions, and configuration options.
 */

import { ReactNode } from 'react';
import {
  Check,
  AlertCircle,
  ExternalLink,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export type IntegrationStatus = 'active' | 'disconnected' | 'error' | 'not_configured';

interface IntegrationCardProps {
  /** Integration display name */
  name: string;
  /** Short description of the integration */
  description: string;
  /** Integration icon component */
  icon: ReactNode;
  /** Background color class for icon container */
  iconBgClass: string;
  /** Current integration status */
  status: IntegrationStatus;
  /** Last sync timestamp (ISO string) */
  lastSyncAt?: string;
  /** Error message if status is 'error' */
  lastError?: string;
  /** Whether the card is in a loading state */
  isLoading?: boolean;
  /** Callback when connect button is clicked */
  onConnect?: () => void;
  /** Callback when configure button is clicked */
  onConfigure?: () => void;
  /** Whether this is the expanded/detailed view */
  isExpanded?: boolean;
}

export function IntegrationCard({
  name,
  description,
  icon,
  iconBgClass,
  status,
  lastSyncAt,
  lastError,
  isLoading,
  onConnect,
  onConfigure,
  isExpanded = false,
}: IntegrationCardProps) {
  const getStatusBadge = () => {
    switch (status) {
      case 'active':
        return (
          <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">
            <Check className="h-3 w-3 mr-1" />
            Connected
          </Badge>
        );
      case 'error':
        return (
          <Badge variant="destructive">
            <AlertCircle className="h-3 w-3 mr-1" />
            Error
          </Badge>
        );
      case 'disconnected':
        return (
          <Badge variant="secondary">
            Disconnected
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            Not Configured
          </Badge>
        );
    }
  };

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${iconBgClass}`}>
              {icon}
            </div>
            <div className="space-y-1">
              <div className="h-5 w-24 bg-muted rounded" />
              <div className="h-4 w-40 bg-muted rounded" />
            </div>
          </div>
          <div className="h-6 w-20 bg-muted rounded" />
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className={isExpanded ? '' : 'hover:shadow-md transition-shadow cursor-pointer'}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${iconBgClass}`}>
            {icon}
          </div>
          <div>
            <CardTitle className="text-base">{name}</CardTitle>
            <CardDescription className="text-sm">
              {description}
            </CardDescription>
          </div>
        </div>
        {getStatusBadge()}
      </CardHeader>

      <CardContent className="pt-0">
        {/* Error message */}
        {status === 'error' && lastError && (
          <p className="text-sm text-destructive mb-3">
            {lastError}
          </p>
        )}

        {/* Last sync info */}
        {status === 'active' && lastSyncAt && (
          <p className="text-xs text-muted-foreground mb-3">
            Last synced: {new Date(lastSyncAt).toLocaleString()}
          </p>
        )}

        {/* Action buttons */}
        <div className="flex gap-2">
          {(status === 'not_configured' || status === 'disconnected' || status === 'error') && onConnect && (
            <Button size="sm" onClick={onConnect} className="gap-2">
              <ExternalLink className="h-4 w-4" />
              Connect
            </Button>
          )}
          {status === 'active' && onConfigure && (
            <Button size="sm" variant="outline" onClick={onConfigure} className="gap-2">
              Configure
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
